import { useMemo } from 'react'

import _ from 'lodash'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useQuizzesStore } from '@/shared/store/useQuizzesStore'
import type { QuizProgress, QuizQuestion } from '@/types/quiz'

import { useUserProgress } from './useUserProgress'

interface UseQuizzesProgressesProps {
  id?: number
}

interface UseQuizzesProgressesReturn {
  quizWasFinished: (id: number) => boolean
  progress: QuizProgress
  rewards: {
    acquiredCoins: number
    acquiredPoints: number
    totalCoins: number
    totalPoints: number
  }
  isLocked: boolean
  lockReason: string
  canRetry: boolean
}

export const useQuizzesProgresses = ({
  id
}: UseQuizzesProgressesProps): UseQuizzesProgressesReturn => {
  const { quizProgress, quizWasFinished } = useUserProgress()

  const { quizzes } = useQuizzesStore()

  const { progress, rewards, isLocked, lockReason, canRetry } =
    useMemo(() => {
      const quiz = quizzes.find(quiz => quiz.id == id)
      const progress = quizProgress.find(
        (progress: QuizProgress) => progress.quiz_id == id
      )
      if (!quiz || !progress) return null

      let isLocked = false
      let lockReason = ''
      let rewards = {
        acquiredCoins: progress?.coin_reward || 0,
        acquiredPoints: progress?.point_reward || 0,
        totalCoins:
          (quiz?.coin_reward || 0) * (progress?.answeres?.length || 1),
        totalPoints:
          (quiz?.point_reward || 0) * (progress?.answeres?.length || 1)
      }
      let canRetry = true
      if (
        quiz.repeat_chances > 0 &&
        progress.attempts_size >= quiz.repeat_chances
      )
        canRetry = false

      return {
        progress,
        rewards,
        isLocked,
        lockReason,
        canRetry
      }
    }, [id, quizzes, quizProgress]) || {}

  return {
    quizWasFinished,
    progress,
    rewards,
    isLocked,
    lockReason,
    canRetry
  }
}
